import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";

/** =======================
 *  Types
 *  ======================= */
export type VerticalTab = {
    id: string;
    label: React.ReactNode;
    content: React.ReactNode | (() => React.ReactNode);
    disabled?: boolean;
};

export interface VerticalTabsProps {
    tabs: VerticalTab[];
    /** Initially active tab id (falls back to first enabled tab) */
    initialTabId?: string;
    /** Height in px or CSS size (e.g., "60vh") */
    height?: number | string;
    /** Left pane width in px or CSS size (e.g., "320px") */
    leftWidth?: number | string;
    /** Optional title shown at the top of the left pane */
    leftTitle?: React.ReactNode;
    /** Optional aria-label for tablist when no leftTitle is provided */
    leftAriaLabel?: string;
    /** Fires when active tab changes */
    onChange?: (tabId: string) => void;
    /** Optional className / style on outer container */
    className?: string;
    style?: React.CSSProperties;
}

/** =======================
 *  Styled components
 *  ======================= */
const Container = styled.div<{ height?: number | string; leftWidth?: number | string }>`
    ${({ height = "480px", leftWidth = "280px" }) => css`
        height: ${typeof height === "number" ? `${height}px` : height};
        grid-template-columns: ${typeof leftWidth === "number" ? `${leftWidth}px` : leftWidth} 1fr;
    `}
    display: grid;
    border: 1px solid ${themeBackgrounds.lightGray};
    background: ${themeBackgrounds.white};
    overflow: hidden;
`;

const LeftPane = styled.div`
    border-right: 1px solid ${themeBackgrounds.lightGray};
    display: flex;
    flex-direction: column;
    background: ${themeBackgrounds.offWhite};
    overflow: hidden;
`;

const LeftHeader = styled.div`
    padding: 10px 12px;
    font-weight: 600;
    color: ${themeColors.primary.richBlack};
    border-bottom: 1px solid ${themeBackgrounds.lightGray};
    background: ${themeBackgrounds.offWhite};
    position: sticky;
    top: 0;
    z-index: 1;
`;

const List = styled.div`
    overflow: auto;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const TabButton = styled.button<{ selected?: boolean }>`
    text-align: left;
    border: none;
    background: transparent;
    padding: 5px 10px;
    color: ${themeColors.primary.richBlack};
    cursor: pointer;
    font-size: ${fontSizes.small};
    line-height: ${lineHeights.default};

    &:hover {
        background: ${themeColors.primary.danaBlue};
        color: ${themeColors.primary.white};
    }

    ${({ selected }) =>
        selected &&
        css`
            background: ${themeColors.primary.danaBlue};
            color: ${themeColors.primary.white};
        `}

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Panel = styled.div`
    padding: 16px;
    overflow: auto;
`;

/** =======================
 *  Component
 *  ======================= */
const VerticalTabs: React.FC<VerticalTabsProps> = ({
    tabs,
    initialTabId,
    height = "480px",
    leftWidth = "280px",
    leftTitle,
    leftAriaLabel,
    onChange,
    className,
    style,
}) => {
    const firstEnabledTabId = useMemo(() => tabs.find(t => !t.disabled)?.id ?? "", [tabs]);

    const [activeId, setActiveId] = useState<string>(() => {
        const isValidInitial = !!initialTabId && tabs.some(t => t.id === initialTabId && !t.disabled);
        return isValidInitial ? (initialTabId as string) : firstEnabledTabId;
    });

    const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);

    // Reset/fallback active id when tabs change
    useEffect(() => {
        if (!tabs.some(t => t.id === activeId && !t.disabled)) {
            const fallback = tabs.find(t => !t.disabled)?.id ?? "";
            setActiveId(fallback);
            if (fallback) {
                onChange?.(fallback);
            }
        }
    }, [tabs]);

    // Update local active id when parent changes initialTabId
    useEffect(() => {
        if (initialTabId && tabs.some(t => t.id === initialTabId && !t.disabled) && initialTabId !== activeId) {
            setActiveId(initialTabId);
        }
    }, [initialTabId]);

    const setActive = (id: string) => {
        setActiveId(id);
        onChange?.(id);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        const currentIndex = tabs.findIndex(t => t.id === activeId);
        let targetIndex = currentIndex;

        const prevEnabled = (from: number) => {
            for (let i = from - 1; i >= 0; i--) {
                if (!tabs[i].disabled) {
                    return i;
                }
            }
            return from;
        };
        const nextEnabled = (from: number) => {
            for (let i = from + 1; i < tabs.length; i++) {
                if (!tabs[i].disabled) {
                    return i;
                }
            }
            return from;
        };

        switch (e.key) {
            case "ArrowUp":
                e.preventDefault();
                targetIndex = prevEnabled(currentIndex);
                break;
            case "ArrowDown":
                e.preventDefault();
                targetIndex = nextEnabled(currentIndex);
                break;
            case "Home":
                e.preventDefault();
                targetIndex = tabs.findIndex(t => !t.disabled);
                break;
            case "End":
                e.preventDefault();
                for (let i = tabs.length - 1; i >= 0; i--) {
                    if (!tabs[i].disabled) {
                        targetIndex = i;
                        break;
                    }
                }
                break;
            default:
                return;
        }

        if (targetIndex !== currentIndex && targetIndex >= 0) {
            const newId = tabs[targetIndex].id;
            setActive(newId);
            btnRefs.current[targetIndex]?.focus();
        }
    };

    const activeTab = tabs.find(t => t.id === activeId);
    const renderContent =
        activeTab && typeof activeTab.content === "function"
            ? (activeTab.content as () => React.ReactNode)()
            : activeTab?.content ?? null;

    const resolveTabContent = (tab?: VerticalTab): React.ReactNode => {
        if (!tab) {
            return null;
        }
    
        if (typeof tab.content === "function") {
            return tab.content();
        }
    
        return tab.content;
    };
            

    return (
        <Container className={className} style={style} height={height} leftWidth={leftWidth}>
            <LeftPane>
                {leftTitle && <LeftHeader>{leftTitle}</LeftHeader>}

                <List role="tablist" aria-orientation="vertical" onKeyDown={onKeyDown}>
                    {tabs.map((tab, idx) => {
                        const selected = tab.id === activeId;
                        return (
                            <TabButton
                                key={tab.id}
                                ref={el => {
                                    btnRefs.current[idx] = el;
                                }} // no-return-assign: fixed
                                id={`tab-${tab.id}`}
                                role="tab"
                                type="button"
                                aria-selected={selected}
                                aria-controls={`panel-${tab.id}`}
                                tabIndex={selected ? 0 : -1}
                                disabled={tab.disabled}
                                selected={selected}
                                onClick={() => setActive(tab.id)}
                            >
                                {tab.label}
                            </TabButton>
                        );
                    })}
                </List>
            </LeftPane>

            <Panel id={`panel-${activeId}`} role="tabpanel" aria-labelledby={`tab-${activeId}`}>
                {resolveTabContent(activeTab)}
            </Panel>
        </Container>
    );
};

export default VerticalTabs;
